import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  MdSecurity,
  MdCheckCircle,
  MdAutoAwesome,
  MdThumbUp,
  MdReviews,
  MdPendingActions,
} from "react-icons/md";
import { storeSettingsService } from "../../../services/storeSettings.service";
import { reviewService } from "../../../services/review.service";
import { t } from "i18next";


export const useReviewSettings = (bussinessUuid) => {
  const [reviewSettings, setReviewSettings] = useState({
    requiresVerification: true,
    autoApproveVerifiedPurchases: true,
    notifyAdminOnNewReview: true,
    minimumRatingForAutoApprove: 3,
    maxReviewsPerDay: 10,
  });

  const [loadingReviewSettings, setLoadingReviewSettings] = useState(false);
  const [pendingReviewsCount, setPendingReviewsCount] = useState(0);
  const [reviewStats, setReviewStats] = useState({
    total: 0,
    approved: 0,
    pending: 0,
  });

  // Load review data on mount
  useEffect(() => {
    if (bussinessUuid) {
      loadReviewData();
    }
  }, [bussinessUuid]);

  const loadReviewData = async () => {
    try {
      setLoadingReviewSettings(true);

      // Load review settings
      const settingsResponse = await storeSettingsService.getStoreSettings(bussinessUuid);
      if (settingsResponse.success && settingsResponse.data && settingsResponse.data.reviewSettings) {
        setReviewSettings(settingsResponse.data.reviewSettings);
      }

      // Load review stats
      const statsResponse = await reviewService.getReviewStats(bussinessUuid);
      if (statsResponse.success && statsResponse.data) {
        setReviewStats(statsResponse.data);
        setPendingReviewsCount(statsResponse.data.pending || 0);
      }
    } catch (error) {
      console.error("Error loading review data:", error);
      toast.error(t("REVIEWS.FAILED_TO_LOAD"));
    } finally {
      setLoadingReviewSettings(false);
    }
  };

  const updateReviewSettings = async (newSettings) => {
    try {
      setLoadingReviewSettings(true);
      
      const response = await storeSettingsService.updateAllSettings(
        bussinessUuid,
        { reviewSettings: newSettings }
      );

      if (response.success) {
        setReviewSettings(newSettings);
        toast.success(t("REVIEWS.SETTINGS_SAVED"));
        
        // Refresh settings
        const settingsResponse = await storeSettingsService.getStoreSettings(bussinessUuid);
        if (settingsResponse.success && settingsResponse.data && settingsResponse.data.reviewSettings) {
          setReviewSettings(settingsResponse.data.reviewSettings);
        }
        
        return true;
      } else {
        toast.error(response.error || t("REVIEWS.FAILED_TO_SAVE"));
        return false;
      }
    } catch (error) {
      console.error("Error saving review settings:", error);
      toast.error(t("REVIEWS.FAILED_TO_SAVE") + ": " + (error.message || t("COMMONONE.UNKNOWN_ERROR")));
      return false;
    } finally {
      setLoadingReviewSettings(false);
    }
  };

  const resetToDefaults = () => {
    const defaultSettings = {
      requiresVerification: true,
      autoApproveVerifiedPurchases: true,
      notifyAdminOnNewReview: true,
      minimumRatingForAutoApprove: 3,
      maxReviewsPerDay: 10,
    };
    setReviewSettings(defaultSettings);
    toast.info(t("REVIEWS.RESET_TO_DEFAULTS"));
    return defaultSettings;
  };

  const refreshStats = async () => {
    try {
      const statsResponse = await reviewService.getReviewStats(bussinessUuid);
      if (statsResponse.success && statsResponse.data) {
        setReviewStats(statsResponse.data);
        setPendingReviewsCount(statsResponse.data.pending || 0);
      }
    } catch (error) {
      console.error("Error refreshing review stats:", error);
    }
  };

  return {
    reviewSettings,
    setReviewSettings,
    loadingReviewSettings,
    pendingReviewsCount,
    reviewStats,
    updateReviewSettings,
    resetToDefaults,
    refreshStats,
    loadReviewData,
  };
};

/**
 * Review Settings Component
 */
export const ReviewSettingsPanel = ({ 
  bussinessUuid,
  reviewSettings,
  setReviewSettings,
  loadingReviewSettings,
  pendingReviewsCount,
  reviewStats,
  onSave,
  onReset,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const handleSettingChange = (key, value) => {
    setReviewSettings(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSave = async () => {
    if (onSave) {
      await onSave(reviewSettings);
    }
  };

  const handleReset = () => {
    if (onReset) {
      const defaults = onReset();
      setReviewSettings(defaults);
    }
  };

  return (
    <div className='space-y-8 animate-fadeIn'>
      <div>
        <h3 className='text-2xl font-bold text-gray-900 dark:text-white mb-2'>
          {t("REVIEWS.REVIEW_MANAGEMENT_SETTINGS")}
        </h3>
        <p className='text-gray-600 dark:text-gray-400'>
          {t("REVIEWS.CONFIGURE_REVIEW_SETTINGS")}
        </p>
      </div>

      {/* Review Verification Settings */}
      <div className='bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm'>
        <div className='flex items-center justify-between mb-6'>
          <div>
            <h4 className='text-xl font-bold text-gray-900 dark:text-white flex items-center gap-3'>
              <MdSecurity className='text-green-600 dark:text-green-400' />
              {t("REVIEWS.VERIFICATION_SYSTEM")}
            </h4>
            <p className='text-gray-600 dark:text-gray-400 mt-1'>
              {t("REVIEWS.VERIFICATION_DESCRIPTION")}
            </p>
          </div>

          {/* Manage All Reviews Button - Only visible when verification is enabled */}
          {reviewSettings.requiresVerification && (
            <button
              onClick={() => navigate(`/${bussinessUuid}/dashboard/allReview`)}
              className='px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 flex items-center gap-2'
            >
              <MdReviews size={18} />
              {t("REVIEWS.MANAGE_PENDING_REVIEWS")}
              {pendingReviewsCount > 0 && (
                <span className='bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center'>
                  {pendingReviewsCount}
                </span>
              )}
            </button>
          )}
        </div>

        <div className='space-y-6'>
          {/* Enable Review Verification Toggle */}
          <div className='p-4 border border-gray-200 dark:border-gray-700 rounded-lg bg-gray-50 dark:bg-gray-900'>
            <div className='flex items-start justify-between'>
              <div className='flex items-start gap-3 flex-1'>
                <div className='flex items-center h-5 mt-1'>
                  <input
                    id='requiresVerification'
                    type='checkbox'
                    checked={reviewSettings.requiresVerification}
                    onChange={(e) => handleSettingChange('requiresVerification', e.target.checked)}
                    className='w-4 h-4 text-green-600 bg-gray-100 border-gray-300 rounded focus:ring-green-500 dark:bg-gray-700 dark:border-gray-600'
                  />
                </div>
                <div className='flex-1'>
                  <label htmlFor='requiresVerification' className='font-medium text-gray-900 dark:text-white flex items-center gap-2'>
                    <MdCheckCircle className='text-green-600 dark:text-green-400' />
                    {t("REVIEWS.ENABLE_VERIFICATION")}
                  </label>
                  <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                    {t("REVIEWS.ENABLE_VERIFICATION_DESCRIPTION")}
                  </p>

                  {/* Current Status Display */}
                  <div className='mt-4 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'>
                    <div className='flex items-center gap-3'>
                      <div className={`p-2 rounded-full ${reviewSettings.requiresVerification ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400" : "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"}`}>
                        {reviewSettings.requiresVerification ? <MdSecurity size={20} /> : <MdCheckCircle size={20} />}
                      </div>
                      <div>
                        <p className='font-medium text-gray-800 dark:text-gray-200'>
                          {reviewSettings.requiresVerification 
                            ? t("REVIEWS.VERIFICATION_REQUIRED") 
                            : t("REVIEWS.INSTANT_POSTING")}
                        </p>
                        <p className='text-sm text-gray-600 dark:text-gray-400'>
                          {reviewSettings.requiresVerification
                            ? t("REVIEWS.VERIFICATION_REQUIRED_DESCRIPTION")
                            : t("REVIEWS.INSTANT_POSTING_DESCRIPTION")}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Auto-approve Settings - Only shown when verification is enabled */}
          {reviewSettings.requiresVerification && (
            <div className='p-4 border border-gray-200 dark:border-gray-700 rounded-lg'>
              <div className='flex items-start justify-between'>
                <div className='flex items-start gap-3 flex-1'>
                  <div className='flex items-center h-5 mt-1'>
                    <input
                      id='autoApproveVerifiedPurchases'
                      type='checkbox'
                      checked={reviewSettings.autoApproveVerifiedPurchases}
                      onChange={(e) => handleSettingChange('autoApproveVerifiedPurchases', e.target.checked)}
                      className='w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600'
                    />
                  </div>
                  <div className='flex-1'>
                    <label htmlFor='autoApproveVerifiedPurchases' className='font-medium text-gray-900 dark:text-white flex items-center gap-2'>
                      <MdAutoAwesome className='text-blue-600 dark:text-blue-400' />
                      {t("REVIEWS.AUTO_APPROVE")}
                    </label>
                    <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                      {t("REVIEWS.AUTO_APPROVE_DESCRIPTION")}
                    </p>

                    {/* Minimum Rating Setting - Only shown when auto-approve is enabled */}
                    {reviewSettings.autoApproveVerifiedPurchases && (
                      <div className='mt-4 space-y-4'>
                        <div>
                          <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                            <div className='flex items-center gap-2'>
                              <MdThumbUp className='text-green-600 dark:text-green-400' />
                              {t("REVIEWS.MINIMUM_RATING")}
                            </div>
                          </label>
                          <p className='text-sm text-gray-600 dark:text-gray-400 mb-3'>
                            {t("REVIEWS.MINIMUM_RATING_DESCRIPTION")}
                          </p>
                          <div className='flex items-center gap-4'>
                            <div className='flex-1'>
                              <input
                                type='range'
                                min='1'
                                max='5'
                                step='0.5'
                                value={reviewSettings.minimumRatingForAutoApprove}
                                onChange={(e) => handleSettingChange('minimumRatingForAutoApprove', parseFloat(e.target.value))}
                                className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-600 dark:bg-gray-700'
                              />
                              <div className='flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-1'>
                                <span>1 ★ ({t("REVIEWS.POOR")})</span>
                                <span className='font-medium'>{reviewSettings.minimumRatingForAutoApprove} ★</span>
                                <span>5 ★ ({t("REVIEWS.EXCELLENT")})</span>
                              </div>
                            </div>
                            <div className='text-center'>
                              <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
                                {reviewSettings.minimumRatingForAutoApprove} ★
                              </div>
                              <div className='text-xs text-gray-500 dark:text-gray-400'>
                                {t("REVIEWS.MINIMUM_RATING")}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Daily Review Limit */}
          <div className='p-4 border border-gray-200 dark:border-gray-700 rounded-lg'>
            <div className='space-y-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'>
                  <div className='flex items-center gap-2'>
                    <MdReviews className='text-amber-600 dark:text-amber-400' />
                    {t("REVIEWS.MAX_REVIEWS_PER_DAY")}
                  </div>
                </label>
                <p className='text-sm text-gray-600 dark:text-gray-400 mb-3'>
                  {t("REVIEWS.MAX_REVIEWS_PER_DAY_DESCRIPTION")}
                </p>
                <div className='flex items-center gap-4'>
                  <div className='flex-1'>
                    <input
                      type='range'
                      min='1'
                      max='20'
                      step='1'
                      value={reviewSettings.maxReviewsPerDay}
                      onChange={(e) => handleSettingChange('maxReviewsPerDay', parseInt(e.target.value))}
                      className='w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-amber-600 dark:bg-gray-700'
                    />
                    <div className='flex justify-between text-sm text-gray-500 dark:text-gray-400 mt-1'>
                      <span>1 {t("REVIEWS.REVIEW_PER_DAY")}</span>
                      <span className='font-medium'>{reviewSettings.maxReviewsPerDay} {t("REVIEWS.REVIEWS_PER_DAY")}</span>
                      <span>20 {t("REVIEWS.REVIEWS_PER_DAY")}</span>
                    </div>
                  </div>
                  <div className='text-center'>
                    <div className='text-2xl font-bold text-amber-600 dark:text-amber-400'>
                      {reviewSettings.maxReviewsPerDay}
                    </div>
                    <div className='text-xs text-gray-500 dark:text-gray-400'>
                      {t("REVIEWS.REVIEWS_PER_DAY")}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Workflow Summary */}
          <div className='bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-6 rounded-2xl border border-blue-100 dark:border-blue-800'>
            <h5 className='text-lg font-semibold text-blue-800 dark:text-blue-300 mb-3'>
              {t("REVIEWS.WORKFLOW_SUMMARY")}
            </h5>
            <div className='space-y-3'>
              {/* Step 1 */}
              <div className='flex items-center gap-3'>
                <div className='w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/50 flex items-center justify-center'>
                  <span className='text-blue-600 dark:text-blue-400 font-bold'>1</span>
                </div>
                <div>
                  <p className='font-medium text-gray-800 dark:text-gray-200'>
                    {t("REVIEWS.CUSTOMER_SUBMITS_REVIEW")}
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    {reviewSettings.notifyAdminOnNewReview 
                      ? `✓ ${t("REVIEWS.ADMIN_WILL_BE_NOTIFIED")}` 
                      : `${t("REVIEWS.ADMIN_NOTIFICATION_DISABLED")}`}
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className='flex items-center gap-3'>
                <div className='w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/50 flex items-center justify-center'>
                  <span className='text-green-600 dark:text-green-400 font-bold'>2</span>
                </div>
                <div>
                  <p className='font-medium text-gray-800 dark:text-gray-200'>
                    {t("REVIEWS.REVIEW_VERIFICATION")}
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    {reviewSettings.requiresVerification ? (
                      <>
                        {reviewSettings.autoApproveVerifiedPurchases
                          ? `✓ ${t("REVIEWS.AUTO_APPROVE_WITH_RATING")} ${reviewSettings.minimumRatingForAutoApprove}+ ${t("REVIEWS.STARS")}`
                          : `✓ ${t("REVIEWS.MANUAL_APPROVAL_REQUIRED")}`}
                      </>
                    ) : (
                      `✗ ${t("REVIEWS.IMMEDIATE_POSTING_NO_VERIFICATION")}`
                    )}
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className='flex items-center gap-3'>
                <div className='w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/50 flex items-center justify-center'>
                  <span className='text-purple-600 dark:text-purple-400 font-bold'>3</span>
                </div>
                <div>
                  <p className='font-medium text-gray-800 dark:text-gray-200'>
                    {t("REVIEWS.REVIEW_GOES_LIVE")}
                  </p>
                  <p className='text-sm text-gray-600 dark:text-gray-400'>
                    {reviewSettings.requiresVerification 
                      ? t("REVIEWS.AFTER_ADMIN_APPROVAL")
                      : t("REVIEWS.IMMEDIATELY_AFTER_SUBMISSION")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Save and Reset Buttons */}
          <div className='pt-6 border-t border-gray-200 dark:border-gray-700'>
            <div className='flex justify-end gap-4'>
              <button
                onClick={handleReset}
                className='px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
              >
                {t("COMMONONE.RESET_TO_DEFAULTS")}
              </button>
              <button
                onClick={handleSave}
                disabled={loadingReviewSettings}
                className={`px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl font-bold hover:from-green-700 hover:to-emerald-700 transition-colors shadow-md ${
                  loadingReviewSettings ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {loadingReviewSettings ? t("COMMONONE.SAVING") : t("REVIEWS.SAVE_REVIEW_SETTINGS")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Review Statistics */}
      <div className='bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm'>
        <div className='flex items-center justify-between mb-6'>
          <h4 className='text-xl font-bold text-gray-900 dark:text-white'>
            {t("REVIEWS.REVIEW_STATISTICS")}
          </h4>
          <div className={`px-3 py-1 rounded-full text-sm font-medium ${
            reviewSettings.requiresVerification 
              ? "bg-green-100 dark:bg-green-900/50 text-green-800 dark:text-green-300" 
              : "bg-blue-100 dark:bg-blue-900/50 text-blue-800 dark:text-blue-300"
          }`}>
            {reviewSettings.requiresVerification 
              ? t("REVIEWS.VERIFICATION_ENABLED") 
              : t("REVIEWS.VERIFICATION_DISABLED")}
          </div>
        </div>

        <div className='grid grid-cols-2 md:grid-cols-4 gap-4'>
          <div className='text-center p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg'>
            <div className='text-2xl font-bold text-indigo-600 dark:text-indigo-400'>{reviewStats.total || 0}</div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>{t("REVIEWS.TOTAL_REVIEWS")}</div>
          </div>
          <div className='text-center p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg'>
            <div className='text-2xl font-bold text-emerald-600 dark:text-emerald-400'>{reviewStats.approved || 0}</div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>{t("REVIEWS.APPROVED_REVIEWS")}</div>
          </div>
          {reviewSettings.requiresVerification && (
            <div className='text-center p-4 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/30 dark:to-orange-900/30 rounded-lg'>
              <div className='text-2xl font-bold text-amber-600 dark:text-amber-400'>{reviewStats.pending || 0}</div>
              <div className='text-sm text-gray-600 dark:text-gray-400'>{t("REVIEWS.PENDING_REVIEWS")}</div>
            </div>
          )}
          <div className='text-center p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/30 dark:to-pink-900/30 rounded-lg'>
            <div className='text-2xl font-bold text-purple-600 dark:text-purple-400'>
              {reviewSettings.requiresVerification 
                ? (reviewSettings.autoApproveVerifiedPurchases ? t("REVIEWS.AUTO_MANUAL") : t("REVIEWS.MANUAL_ONLY")) 
                : t("REVIEWS.INSTANT")}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>{t("REVIEWS.APPROVAL_TYPE")}</div>
          </div>
        </div>

        {/* Current Status Message */}
        <div className='mt-6 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700'>
          <div className='flex items-center gap-3'>
            <div className={`p-2 rounded-full ${
              reviewSettings.requiresVerification 
                ? "bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400" 
                : "bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400"
            }`}>
              {reviewSettings.requiresVerification ? <MdPendingActions size={20} /> : <MdCheckCircle size={20} />}
            </div>
            <div>
              <p className='font-medium text-gray-800 dark:text-gray-200'>
                {t("REVIEWS.CURRENT_STATUS")}: {reviewSettings.requiresVerification 
                  ? t("REVIEWS.VERIFICATION_REQUIRED") 
                  : t("REVIEWS.INSTANT_POSTING")}
              </p>
              <p className='text-sm text-gray-600 dark:text-gray-400 mt-1'>
                {reviewSettings.requiresVerification
                  ? t("REVIEWS.CURRENT_STATUS_VERIFICATION_DESCRIPTION")
                  : t("REVIEWS.CURRENT_STATUS_INSTANT_DESCRIPTION")}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};